import type { ApprovalMode, ClFarmAlmType, EmitEvent } from "@/domains/core"
import type { EventEmitter } from "@/domains/library"
import type { Address, PublicClient } from "viem"
import type { Config } from "wagmi"
import { ClFarmStore } from "@/domains/cl-farm/cl-farm-store"
import { approveOwnerSpenderAllowance, stakeClFarmLiquidity } from "@/domains/on-chain"
import { erc20Abi, formatUnits, parseUnits } from "viem"

type StakeClFarmLiquidityInteraction = {
  amount: bigint
  approvalMode: ApprovalMode
  chainId: number
  config: Config
  connectedAddress: Address
  emitter: EventEmitter<EmitEvent>
  clFarm: ClFarmStore
  publicClient: PublicClient
  onSuccess: () => void
}

export const stakeClFarmLiquidityInteraction = async ({
  amount,
  approvalMode,
  chainId,
  clFarm,
  config,
  connectedAddress,
  emitter,
  publicClient,
  onSuccess,
}: StakeClFarmLiquidityInteraction) => {
  if (!connectedAddress) {
    return emitter.emit("error-not-connected", {
      title: "Not connected",
      description: "In order to interact with this app, you first must connect your wallet.",
      variant: "error",
    })
  }
  if (!clFarm) return

  if (clFarm.onChainUserBalance < amount) {
    emitter.emit("error-insufficient-balance-lp", {
      title: "Insufficient balance",
      description: `You do not have enough ${clFarm.displayName} in your wallet. You are trying to withdraw ${clFarm.formatUnits(amount - clFarm.onChainUserBalance)} more than available.`,
      variant: "error",
    })
    return
  }

  //#region STEP TWO - Does the user need to provide an allowance?
  const allowance = await publicClient.readContract({
    address: clFarm.providerLp.address.toViemAddress(),
    abi: erc20Abi,
    functionName: "allowance",
    args: [connectedAddress, clFarm.farmAddress.toViemAddress()],
  })

  if (allowance < amount) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${clFarm.formatUnits(amount)} ${clFarm.displayName}`,
      variant: "info",
    })

    const approval = await approveOwnerSpenderAllowance({
      account: connectedAddress,
      chainId,
      config,
      contractAddress: clFarm.providerLp.address.toViemAddress(),
      spenderAddress: clFarm.farmAddress.toViemAddress(),
      spenderAmount: amount,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (approval.success) {
      emitter.emit("approve-token-allowance", {
        title: "Approval successful",
        description: "You may now proceed to stake",
        variant: "success",
      })
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error ?? "unknown error",
        variant: "error",
      })
      return
    }
  }
  //#endregion

  //#region STEP THREE: Withdraw the CL Farm Liquidity from the Farm
  const stakeClFarmLiquidityResult = await stakeClFarmLiquidity({
    account: connectedAddress,
    chainId,
    config,
    farmAddress: clFarm.farmAddress.toViemAddress(),
    amount: amount,
  })

  if (stakeClFarmLiquidityResult.success) {
    onSuccess?.()
    emitter.emit("success-stake-liquidity", {
      title: `Stake  ${clFarm.displayName}`,
      description: `Staked ${clFarm.formatUnits(amount)} ${clFarm.displayName} tokens`,
      variant: "success",
    })
  } else {
    emitter.emit("error-stake-liquidity", {
      title: `Unable to stake ${clFarm.formatUnits(amount)} ${clFarm.displayName} tokens`,
      description: stakeClFarmLiquidityResult.error || "Unknown error",
      variant: "error",
    })
  }
  //#endregion

  return stakeClFarmLiquidityResult
}
